import localForage from "localforage";
import { call, fork, put, putResolve, race, select, take, takeEvery, takeLatest } from "redux-saga/effects";

import { maybeCloseDialogs, openDialog } from "../dialogs";
import { sendNotification } from "../notifications/notifications.slice";
import { fetchProposalTemplate } from "../template";
import {
  applyForFinancing,
  fetchProposalData,
  recalculatePricing,
  refreshDesignData,
  submitOptimization,
  syncDesignData,
} from "./proposal.actions";
import proposalSlice, { startRecalculatePricingSaga, startSubmitOptimizationSaga } from "./proposal.slice";

function* fetchTemplateSaga(proposalId: string) {
  const action = yield call(fetchProposalTemplate, proposalId);
  yield put(action);
}

function* fetchProposalSaga(proposalId: string) {
  const action = yield call(fetchProposalData, proposalId);
  yield put(action);
}

function* recalculatePricingSaga(action: ReturnType<typeof startRecalculatePricingSaga>) {
  const recalculatePricingAction = yield call(recalculatePricing, action.payload);
  yield putResolve(recalculatePricingAction);
}

function* optimizeDesignSaga(action: ReturnType<typeof startSubmitOptimizationSaga>) {
  const optimizeAction = yield call(submitOptimization, action.payload);
  yield putResolve(optimizeAction);
}

type ApplyForFinancingResult =
  | {
      fulfilled: ReturnType<typeof applyForFinancing.fulfilled>;
      rejected: undefined;
    }
  | {
      fulfilled: undefined;
      rejected: ReturnType<typeof applyForFinancing.rejected>;
    };

function* applyForFinancingSaga(action: ReturnType<typeof applyForFinancing.pending>) {
  const result: ApplyForFinancingResult = yield race({
    fulfilled: take(applyForFinancing.fulfilled.type),
    rejected: take(applyForFinancing.rejected.type),
  });

  if (result.fulfilled) {
    const { payload } = result.fulfilled;

    window.location.href = payload.redirectUrl;
  } else {
    const { payload } = result.rejected!;

    yield put(
      sendNotification({
        id: "apply-for-financing",
        type: "error",
        title: "Error",
        body: payload?.message,
      })
    );
  }
}

// 30 days
const LOCK_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;
const EXPIRY_KEY = "__enerflo_expiry" as const;

type ValueWithExpiry = Record<typeof EXPIRY_KEY, number> & {
  value: false;
};

function isValueWithExpiry(value: unknown): value is ValueWithExpiry {
  return (
    typeof value === "object" &&
    value !== null &&
    EXPIRY_KEY in value &&
    typeof (value as Record<string, unknown>)[EXPIRY_KEY] === "number"
  );
}

function* cleanExpiredEntries(store: LocalForage) {
  const keys: string[] = yield store.keys();

  for (const key of keys) {
    const entry: unknown = yield store.getItem(key);

    if (!isValueWithExpiry(entry) || entry[EXPIRY_KEY] < Date.now()) {
      yield store.removeItem(key);
    }
  }
}

function* restoreLockStatusSaga(store: LocalForage, cacheKey: string) {
  yield call(cleanExpiredEntries, store);

  const storedValue: unknown = yield store.getItem(cacheKey);

  if (storedValue !== null) {
    yield put(proposalSlice.actions.unlock());
  }
}

function* persistLocked(store: LocalForage) {
  const cacheKey = yield select(state => state.proposal.reportHash);
  const isLocked = yield select(state => state.proposal.locked);
  if (!cacheKey) return;
  if (isLocked) {
    yield store.removeItem(cacheKey);
  } else {
    yield store.setItem(cacheKey, { value: false, [EXPIRY_KEY]: Date.now() + LOCK_EXPIRY_MS });
  }
}

export function* watchLockStatusSaga(store: LocalForage, proposalId: string) {
  yield call(restoreLockStatusSaga, store, proposalId);

  yield takeEvery(proposalSlice.actions.toggleLock.type, persistLocked, store);
}

function* startProposalAppListeners(proposalId: string, localForage: LocalForage) {
  yield fork(watchLockStatusSaga, localForage, proposalId);

  yield takeLatest(proposalSlice.actions.startRecalculatePricingSaga, recalculatePricingSaga);
  yield takeLatest(proposalSlice.actions.startSubmitOptimizationSaga, optimizeDesignSaga);

  yield takeLatest(submitOptimization.fulfilled, function* () {
    yield put(maybeCloseDialogs(["OPTIMIZE_BY_METRIC", "MANAGE_BATTERIES"]));
    // Refetch the template slides whenever the proposal data changes
    yield put(fetchProposalTemplate(proposalId));
  });
  yield takeLatest(recalculatePricing.fulfilled, function* () {
    yield put(
      maybeCloseDialogs([
        "MANAGE_LENDER",
        "MANAGE_GROSS_SYSTEM_COST",
        "MANAGE_SYSTEM_COST",
        "MANAGE_TAX_CREDIT",
        "MANAGE_UTILITY_RATE_SCHEDULE",
      ])
    );
  });
  yield takeLatest(applyForFinancing.pending, applyForFinancingSaga);

  yield takeLatest(
    [fetchProposalData.fulfilled, refreshDesignData.fulfilled, syncDesignData.fulfilled],
    function* (action) {
      if (action.payload.solarCosts.lenderRate === null) {
        yield put(openDialog("MANAGE_LENDER"));
      }
    }
  );
}

const createLocalStore = () =>
  localForage.createInstance({
    name: "proposal-lock",
    driver: localForage.LOCALSTORAGE,
  });

export function* proposalSaga(store = createLocalStore()) {
  const initAction: ReturnType<typeof proposalSlice.actions.init> = yield take(proposalSlice.actions.init.type);
  const proposalId = initAction.payload.id;

  yield fork(startProposalAppListeners, proposalId, store);

  yield fork(fetchTemplateSaga, proposalId);
  yield fork(fetchProposalSaga, proposalId);
}
